/** Named appointment slots from product requirements (display order). */
export const APPOINTMENT_SLOTS = {
  ALLDAY: 'All Day',
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

/**
 * Default schedule grid / persistence keys (requirements JSON includes EVENING).
 * Row order: All Day, Morning, Afternoon, Evening.
 */
export const SCHEDULE_SLOT_KEYS = ['ALLDAY', 'MORNING', 'AFTERNOON', 'EVENING'];

export const SCHEDULE_SLOT_LABELS = {
  ALLDAY: 'All Day',
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

/** Slot keys for requirements.md custom rule JSON (`ALL_DAY` vs grid `ALLDAY`). */
export const CUSTOM_RULE_SLOT_KEYS = [
  'ALL_DAY',
  'MORNING',
  'AFTERNOON',
  'EVENING',
];

export const CUSTOM_RULE_SLOT_LABELS = {
  ALL_DAY: 'All Day',
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

/** Weekday abbreviations used in custom schedule rule JSON. */
export const RULE_JSON_WEEKDAY_KEYS = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
];

export const RULE_JSON_WEEKDAY_LABELS = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun',
};

export const WEEKDAY_KEYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

export const WEEKDAY_LABELS = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

export const WEEKDAY_SHORT_LABELS = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

/** Persisted per-weekday on `schedule[weekday]` alongside slot booleans. */
export const CUTOFF_KEY = 'cutoff';

/** Default cutoff time stored as top-of-hour only (`HH:00`). */
export const DEFAULT_CUTOFF_TIME = '12:00';

/** Selectable cutoff hours (24h). */
export const CUTOFF_HOUR_RANGE = Array.from({ length: 24 }, (_, i) => i);

/** Dropdown label: hour only in 24h notation (minutes always :00). */
export function formatCutoffHourOption24h(hour) {
  const h = Number(hour);
  if (!Number.isInteger(h) || h < 0 || h > 23) return '12:00';
  return `${String(h).padStart(2, '0')}:00`;
}

/**
 * Parse stored `HH:00` / legacy `HH:mm` into hour 0–23.
 * @param {string} [timeStr]
 * @returns {number}
 */
export function parseCutoffHourFromStoredTime(timeStr) {
  const s = typeof timeStr === 'string' ? timeStr.trim() : '';
  const m = /^([01]?\d|2[0-3])(?::([0-5]\d))?$/.exec(s);
  if (!m) return 12;
  const h = Number(m[1]);
  if (!Number.isInteger(h) || h < 0 || h > 23) return 12;
  return h;
}

/**
 * When no cutoff is stored, Tue–Sun default to previous calendar day at noon;
 * Monday defaults to the previous Friday at noon (3 calendar days earlier).
 * @param {string} wk key from WEEKDAY_KEYS
 * @returns {{ daysBefore: number, time: string }}
 */
export function getDefaultCutoffForWeekday(wk) {
  if (wk === 'MONDAY') {
    return { daysBefore: 3, time: DEFAULT_CUTOFF_TIME };
  }
  return { daysBefore: 1, time: DEFAULT_CUTOFF_TIME };
}

/** Calendar weekday name for the cutoff day given job weekday and offset. */
export function getCalendarDayLabelForJobWeekday(jobWeekdayKey, daysBefore) {
  const idx = WEEKDAY_KEYS.indexOf(jobWeekdayKey);
  if (idx < 0) return '';
  const targetIdx = (idx - daysBefore + 49) % 7;
  return WEEKDAY_LABELS[WEEKDAY_KEYS[targetIdx]];
}

export function getCutoffDayOptionLabel(jobWeekdayKey, daysBefore) {
  const dayName = getCalendarDayLabelForJobWeekday(jobWeekdayKey, daysBefore);
  if (daysBefore === 0) return `Same Day (${dayName})`;
  if (daysBefore === 1) return `Previous Day (${dayName})`;
  return `${daysBefore} Days Before (${dayName})`;
}

const TIME_LIKE_RE = /^([01]?\d|2[0-3])(?::([0-5]\d))?$/;

export function normalizeCutoffValue(raw, jobWeekdayKey) {
  const fallback = getDefaultCutoffForWeekday(jobWeekdayKey);
  if (!raw || typeof raw !== 'object') return { ...fallback };
  let daysBefore = Number(raw.daysBefore);
  if (!Number.isInteger(daysBefore) || daysBefore < 0 || daysBefore > 6) {
    daysBefore = fallback.daysBefore;
  }
  let time = typeof raw.time === 'string' ? raw.time.trim() : fallback.time;
  if (!TIME_LIKE_RE.test(time)) {
    time = fallback.time;
  } else {
    const hour = parseCutoffHourFromStoredTime(time);
    time = `${String(hour).padStart(2, '0')}:00`;
  }
  return { daysBefore, time };
}

export function formatCutoffTimeDisplay(timeStr) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(timeStr);
  if (!m) return timeStr;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

export function formatCutoffSummary(jobWeekdayKey, cutoff) {
  const c = normalizeCutoffValue(cutoff, jobWeekdayKey);
  const dayName = getCalendarDayLabelForJobWeekday(jobWeekdayKey, c.daysBefore);
  return `${dayName} ${formatCutoffTimeDisplay(c.time)}`;
}
